import React, { useState, useCallback, useMemo } from 'react';
import { Badge, Alert, Form, Button, Card } from 'react-bootstrap';
import { DataTable } from '../../components/common/DataTable';
import type { ColumnConfig } from '../../components/common/DataTable/types';
import { useAuth } from '../../hooks/useAuth';
import { useQueryClient } from '@tanstack/react-query';
import {
  getPaymentsRequests,
  usePostInvoicePaymentsPaymentIdRefresh,
  useGetUsersId,
} from '../../api/generated/arrayscapeGamingDashboardAPI';
import type { PaymentRequestDetail, GetPaymentsRequestsParamsStatus } from '../../api/generated/model';
import { usePaginatedQuery } from '../../hooks/usePaginatedQuery';

const FETCH_LIMIT = 25;

const paymentStatusBadge = (status?: string, label?: string) => {
  const variants: Record<string, string> = {
    pending: 'secondary',
    requested: 'warning',
    approved: 'success',
    processing: 'info',
    sent: 'success',
    rejected: 'danger',
    cancelled: 'secondary',
    failed: 'danger',
  };
  return (
    <Badge bg={variants[status || ''] || 'secondary'} className="text-capitalize">
      {label || status || 'unknown'}
    </Badge>
  );
};

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatPeriod = (start?: string, end?: string): string => {
  if (!start || !end) return '';
  const s = new Date(start + 'T00:00:00');
  const e = new Date(end + 'T00:00:00');
  const sMonth = s.toLocaleDateString('en-US', { month: 'short' });
  const eMonth = e.toLocaleDateString('en-US', { month: 'short' });
  return `${sMonth} ${s.getDate()} - ${eMonth} ${e.getDate()}, ${e.getFullYear()}`;
};

const PayrollPayoutsTab: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data: userData } = useGetUsersId(user?.id || '', { query: { enabled: !!user?.id } });

  // Filter and sort state
  const [statusFilter, setStatusFilter] = useState<GetPaymentsRequestsParamsStatus[]>([]);
  const [sortField, setSortField] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Row identity. Shared with the hook's duplicate-detection and DataTable.
  const getRowKey = useCallback((item: PaymentRequestDetail) => item.id || '', []);

  // Build query params
  const buildQueryParams = useCallback(
    (cursor: string | undefined) =>
      cursor
        ? { cursor, limit: FETCH_LIMIT }
        : {
            limit: FETCH_LIMIT,
            sort_field: sortField,
            sort_direction: sortDirection,
            ...(statusFilter.length > 0 ? { status: statusFilter } : {}),
          },
    [statusFilter, sortField, sortDirection]
  );

  const {
    allItems: allRequests,
    totalCount,
    isLoading,
    error,
    hasMore,
    handleLoadMore,
    setAllItems: setAllRequests,
  } = usePaginatedQuery<PaymentRequestDetail, Awaited<ReturnType<typeof getPaymentsRequests>>>({
    queryKey: 'payments-requests',
    buildQueryParams,
    queryFn: (params) => getPaymentsRequests(params as any),
    getRowKey,
  });

  const refreshPaymentMutation = usePostInvoicePaymentsPaymentIdRefresh();

  const handleSortChange = useCallback((field: string, direction: 'asc' | 'desc') => {
    setSortField(field);
    setSortDirection(direction);
  }, []);

  const handleRefresh = useCallback(async (paymentId: string) => {
    try {
      const updated = await refreshPaymentMutation.mutateAsync({ paymentId });
      setAllRequests(prev => prev.map(r => r.id === paymentId ? updated : r));
    } catch {
      // Error handled by mutation state
    }
  }, [refreshPaymentMutation, setAllRequests]);

  const columns: ColumnConfig<PaymentRequestDetail>[] = useMemo(() => [
    {
      key: 'user',
      label: 'Employee',
      width: 180,
      renderCell: (item) => {
        const ts = item.timesheet;
        if (!ts?.user) return <span className="text-muted">-</span>;
        return (
          <div>
            <div className="fw-semibold">{ts.user.first_name} {ts.user.last_name}</div>
            <small className="text-muted">{ts.user.email}</small>
          </div>
        );
      },
    },
    {
      key: 'timesheet_period',
      label: 'Period',
      width: 180,
      renderCell: (item) => {
        const ts = item.timesheet;
        if (!ts?.period_start || !ts?.period_end) return <span className="text-muted">-</span>;
        return formatPeriod(ts.period_start, ts.period_end);
      },
    },
    {
      key: 'amount',
      label: 'Amount',
      width: 100,
      sortable: true,
      renderCell: (item) => item.amount != null ? `$${item.amount.toFixed(2)}` : '-',
    },
    {
      key: 'status',
      label: 'Payment Status',
      width: 140,
      sortable: true,
      renderCell: (item) => paymentStatusBadge(item.status, item.status_label),
    },
    {
      key: 'payment_method',
      label: 'Method',
      width: 80,
      renderCell: (item) => item.payment_method?.toUpperCase() || '-',
    },
    {
      key: 'mercury_status',
      label: 'Mercury Status',
      width: 130,
      renderCell: (item) => item.mercury_status || '-',
    },
    {
      key: 'error_message',
      label: 'Error',
      width: 200,
      minWidth: 200,
      renderCell: (item) => item.error_message ? (
        <span className="text-danger" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {item.error_message}
        </span>
      ) : '-',
    },
    {
      key: 'created_at',
      label: 'Created',
      width: 160,
      sortable: true,
      renderCell: (item) => formatDate(item.created_at),
    },
    {
      key: 'updated_at',
      label: 'Updated',
      width: 160,
      sortable: true,
      renderCell: (item) => formatDate(item.updated_at),
    },
    {
      key: 'actions',
      label: '',
      width: 50,
      renderCell: (item) => (
        <Button
          variant="outline-secondary"
          size="sm"
          disabled={refreshPaymentMutation.isPending}
          onClick={(e) => {
            e.stopPropagation();
            if (item.id) handleRefresh(item.id);
          }}
          title="Refresh payment status"
        >
          <i className="bi bi-arrow-clockwise"></i>
        </Button>
      ),
    },
  ], [handleRefresh, refreshPaymentMutation.isPending]);

  return (
    <>
      {error && (
        <Alert variant="danger" className="mb-3">
          {(error as any)?.response?.data?.message || 'Failed to load payment requests'}
        </Alert>
      )}

      <Card className="d-flex flex-column flex-grow-1" style={{ minHeight: '240px', overflow: 'hidden' }}>
        <Card.Header className="bg-primary text-white flex-shrink-0">
          <div className="d-flex align-items-center gap-3">
            <Form.Select
              size="sm"
              style={{ width: 'auto' }}
              value={statusFilter.length === 0 ? 'all' : statusFilter[0]}
              onChange={e => {
                const val = e.target.value;
                setStatusFilter(val === 'all' ? [] : [val as GetPaymentsRequestsParamsStatus]);
              }}
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="requested">Requested</option>
              <option value="approved">Approved</option>
              <option value="processing">Processing</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
            </Form.Select>
            <span className="text-white-50">
              {allRequests.length > 0 && (
                <small>{totalCount ?? allRequests.length} total</small>
              )}
            </span>
          </div>
        </Card.Header>
        <Card.Body className="flex-grow-1 overflow-hidden p-0" style={{ minHeight: 0 }}>
          <DataTable
            data={allRequests}
            columns={columns}
            getRowKey={getRowKey}
            sortConfig={{ field: sortField, direction: sortDirection }}
            onSortChange={handleSortChange}
            pagination={{ hasMore, limit: FETCH_LIMIT }}
            onLoadMore={handleLoadMore}
            isLoading={isLoading}
            preferencesKey="payroll-payouts"
            userId={user?.id}
            userPreferences={userData?.preferences}
            maxHeight="100%"
            enableColumnReorder={true}
            enableColumnResize={true}
            enableColumnVisibility={true}
            enableStickyHeader={true}
            emptyState={
              <div className="text-center py-4 text-muted">
                <i className="bi bi-credit-card fs-1 d-block mb-2"></i>
                No payouts found.
              </div>
            }
          />
        </Card.Body>
      </Card>
    </>
  );
};

export default PayrollPayoutsTab;
